import type { TFunction } from 'i18next';

export interface RelocationApiError {
  code?: string;
  message: string;
  field?: string;
}

/**
 * Forma del error que devuelve el backend:
 * { "error": { "error_code": "...", "message": "...", "field": "", "type": "validation" } }
 */
export const extractRelocationError = (error: any): RelocationApiError => {
  const data = error?.response?.data;
  const apiError = data?.error;
  const code: string | undefined = apiError?.error_code || apiError?.code;
  const message: string = apiError?.message || data?.message || error?.message || '';
  const field: string | undefined = apiError?.field;
  return { code, message, field };
};

interface MapErrorContext {
  t: TFunction<'relocationProcessModule'>;
  locationCode?: string;
  productCode?: string;
  qty?: number;
}

/**
 * Convierte los error_code del backend a mensajes UX amigables.
 * Mantiene el mensaje crudo del backend como fallback.
 */
export const mapRelocationErrorMessage = (
  error: any,
  { t, locationCode, productCode, qty }: MapErrorContext,
): string => {
  const { code, message } = extractRelocationError(error);

  switch (code) {
    case 'INSUFFICIENT_STOCK':
      return t('errors.insufficient_stock', {
        defaultValue: 'La ubicación {{locationCode}} no tiene stock del producto {{productCode}}.',
        locationCode: locationCode || '-',
        productCode: productCode || '-',
      });
    case 'INSUFFICIENT_STOCK_AT_LOCATION':
      return t('errors.insufficient_stock_at_location', {
        defaultValue: 'La cantidad ({{qty}}) supera el stock disponible en la ubicación {{locationCode}}.',
        qty: qty ?? '-',
        locationCode: locationCode || '-',
      });
    case 'CART_CANNOT_BE_SOURCE_LOCATION':
      return t('errors.cart_cannot_be_source', {
        defaultValue: 'No puedes usar el carrito como ubicación de origen.',
      });
    case 'RELOCATION_NOT_DRAFT':
      return t('errors.not_draft', {
        defaultValue: 'Esta reubicación ya no admite cambios (no está en borrador).',
      });
    case 'RELOCATION_HAS_NO_LINES':
      return t('errors.no_lines', {
        defaultValue: 'No puedes finalizar una reubicación sin productos.',
      });
    case 'CART_ALREADY_IN_USE':
      return t('errors.cart_in_use', {
        defaultValue: 'El carrito ya está siendo usado por otra reubicación.',
      });
    default:
      return message || t('errors.unexpected', { defaultValue: 'Error inesperado' });
  }
};
